import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, Save, Upload, Film, X, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { uploadService } from '@/services/uploadService';
import { genericVideoService } from '@/services/genericVideoService';
import { AdminPageShell, PageHeader, PageLoading } from '@/components/admin/shell';
import { AdminButton } from '@/components/admin/shell/AdminButton';
import type { GenericVideoFormData } from '@/types/genericVideo';

export function GenericVideoFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = id !== undefined && id !== 'new';
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [portadillaEnabled, setPortadillaEnabled] = useState(true);
  const [portadillaText, setPortadillaText] = useState('Gracias por visitarnos, {nombre}');
  const [videoBlobId, setVideoBlobId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [hasRemovedVideo, setHasRemovedVideo] = useState(false);

  const { data: video, isLoading } = useQuery({
    queryKey: ['admin-generic-video', id],
    queryFn: () => genericVideoService.list().then(vs => vs.find(v => v.id === id)!),
    enabled: isEditing,
  });

  useEffect(() => {
    if (video) {
      setName(video.name);
      setDescription(video.description || '');
      setPortadillaEnabled(video.portadilla_enabled);
      setPortadillaText(video.portadilla_text);
    }
  }, [video]);

  const existingVideoUrl = video?.video_file_url && !hasRemovedVideo ? video.video_file_url : null;

  const uploadVideo = useCallback(async (file: File) => {
    setIsUploading(true);
    setUploadProgress(0);
    try {
      const blobId = await uploadService.uploadFile(file, (progress) => {
        setUploadProgress(progress);
      });
      setVideoBlobId(blobId);
      setHasRemovedVideo(false);
      toast.success('Video subido correctamente');
    } catch {
      toast.error('Error al subir el video');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  }, []);

  const handleRemoveVideo = () => {
    setVideoBlobId(null);
    setHasRemovedVideo(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const saveMutation = useMutation({
    mutationFn: (data: GenericVideoFormData) => {
      if (isEditing && id) return genericVideoService.update(id, data);
      return genericVideoService.create(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-generic-videos'] });
      toast.success(isEditing ? 'Video actualizado' : 'Video creado');
      navigate('/admin/generic-videos');
    },
    onError: () => toast.error('Error al guardar'),
  });

  const handleSave = () => {
    if (!name.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    if (!isEditing && !videoBlobId && !existingVideoUrl) {
      toast.error('Debes subir un video');
      return;
    }
    saveMutation.mutate({
      name: name.trim(),
      description: description.trim() || undefined,
      portadillaEnabled,
      portadillaText,
      videoBlobId: videoBlobId || undefined,
    });
  };

  if (isLoading) return <PageLoading />;

  const isSubmitting = saveMutation.isPending;

  return (
    <AdminPageShell>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/admin/generic-videos')}
            className="min-h-11 min-w-11"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <PageHeader
            title={isEditing ? 'Editar Video Genérico' : 'Nuevo Video Genérico'}
            subtitle={isEditing ? 'Actualiza la información del video' : 'Añade un nuevo video genérico'}
          />
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Información del Video</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label htmlFor="name">Nombre *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Video Navidad 2026"
                className="min-h-11"
              />
            </div>

            <div>
              <Label htmlFor="description">Descripción</Label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descripción opcional..."
                rows={3}
                className="w-full min-h-[100px] px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Archivo de Video {!isEditing && '*'}</CardTitle>
          </CardHeader>
          <CardContent>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/mp4,video/quicktime"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) uploadVideo(file);
              }}
              disabled={isUploading}
              className="hidden"
            />

            {existingVideoUrl && !videoBlobId && (
              <div className="space-y-4">
                <div className="relative rounded-lg overflow-hidden bg-black">
                  <video
                    src={existingVideoUrl}
                    className="w-full max-h-[360px] object-contain"
                    controls
                    muted
                    playsInline
                  />
                  <div className="absolute top-3 left-3 bg-purple-600/90 backdrop-blur-sm text-white text-xs font-bold px-2.5 py-1 rounded-md flex items-center gap-1.5">
                    <Film className="w-3.5 h-3.5" />
                    VIDEO
                  </div>
                </div>

                {isUploading ? (
                  <div className="flex items-center gap-4 p-4 rounded-lg bg-blue-50 border border-blue-100">
                    <div className="flex-1">
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-blue-700 font-medium">Reemplazando video...</span>
                        <span className="text-blue-600">{uploadProgress}%</span>
                      </div>
                      <div className="w-full bg-blue-200 rounded-full h-1.5">
                        <div
                          className="bg-blue-600 h-full rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex-1 min-h-11"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Reemplazar video
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleRemoveVideo}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 min-h-11"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Eliminar
                    </Button>
                  </div>
                )}
              </div>
            )}

            {videoBlobId && !existingVideoUrl && (
              <div className="flex items-center justify-between p-4 rounded-lg bg-green-50 border border-green-200">
                <div className="flex items-center gap-2 text-sm text-green-700">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="font-medium">Nuevo video listo para guardar</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveVideo}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <X className="w-4 h-4 mr-1.5" />
                  Quitar
                </Button>
              </div>
            )}

            {!existingVideoUrl && !videoBlobId && (
              isUploading ? (
                <div className="flex flex-col items-center gap-4 p-8 rounded-lg border-2 border-blue-200 bg-blue-50">
                  <div className="w-12 h-12 rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin" />
                  <div className="text-center">
                    <p className="text-sm font-medium text-blue-700">Subiendo video...</p>
                    <p className="text-2xl font-bold text-blue-800 mt-1">{uploadProgress}%</p>
                  </div>
                  <div className="w-56 bg-blue-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-full rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full border-2 border-dashed border-gray-300 rounded-lg p-10 text-center hover:border-gray-400 hover:bg-gray-50 transition-colors"
                >
                  <Film className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm font-medium text-gray-700">
                    Haz clic para seleccionar un video
                  </p>
                  <p className="text-xs text-gray-500 mt-1">MP4, MOV · máx. 50MB</p>
                </button>
              )
            )}

            {existingVideoUrl && videoBlobId && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <span className="font-medium">Nuevo video listo para guardar (reemplazará el actual)</span>
              </div>
            )}

            <p className="text-sm text-gray-500 mt-2">
              El video se usará como base para generar los recuerdos personalizados.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Portadilla</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <Switch checked={portadillaEnabled} onCheckedChange={setPortadillaEnabled} />
              <Label>Mostrar portadilla personalizada</Label>
            </div>
            {portadillaEnabled && (
              <div>
                <Label htmlFor="portadillaText">Texto de la portadilla</Label>
                <Input
                  id="portadillaText"
                  value={portadillaText}
                  onChange={(e) => setPortadillaText(e.target.value)}
                  placeholder="Gracias por visitarnos, {nombre}"
                  className="min-h-11"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Usa {'{nombre}'} para insertar el nombre del cliente
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex gap-4">
          <AdminButton
            type="button"
            variant="primary"
            isLoading={isSubmitting}
            loadingText="Guardando..."
            onClick={handleSave}
            disabled={isUploading}
            className="flex-1 md:flex-initial"
          >
            <Save className="mr-2 h-4 w-4" />
            {isEditing ? 'Actualizar Video' : 'Crear Video'}
          </AdminButton>
          <AdminButton
            type="button"
            variant="ghost"
            onClick={() => navigate('/admin/generic-videos')}
            disabled={isSubmitting}
          >
            Cancelar
          </AdminButton>
          {isEditing && (
            <AdminButton
              type="button"
              variant="danger"
              onClick={() => {
                if (confirm('¿Eliminar este video genérico?')) {
                  genericVideoService.delete(id!).then(() => {
                    queryClient.invalidateQueries({ queryKey: ['admin-generic-videos'] });
                    toast.success('Video eliminado');
                    navigate('/admin/generic-videos');
                  });
                }
              }}
              disabled={isSubmitting}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Eliminar
            </AdminButton>
          )}
        </div>
      </div>
    </AdminPageShell>
  );
}
